import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const LABEL_COLORS = {
  indigo: "bg-blue-500", violet: "bg-slate-600", pink: "bg-sky-500",
  emerald: "bg-emerald-500", amber: "bg-amber-500", sky: "bg-sky-500",
  rose: "bg-rose-500", red: "bg-red-500",
};

export default function BoardFilters({ filters, setFilters, labels }) {
  const { t } = useTranslation();
  const active = filters.q || filters.labelIds.length || filters.priority || filters.dueRange;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className="bg-white/20 hover:bg-white/30 text-white border-0"
          data-testid="board-filters-trigger"
        >
          <Filter className="h-4 w-4 mr-2" />
          {t("filters.title")}{active ? " •" : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 glass-panel rounded-2xl">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">{t("filters.search")}</label>
            <Input
              data-testid="filter-search"
              value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder={t("filters.searchPh")} className="mt-1 bg-white/60 dark:bg-slate-900/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{t("filters.priority")}</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {["low", "medium", "high", "urgent"].map((p) => (
                <button
                  key={p}
                  data-testid={`filter-priority-${p}`}
                  onClick={() => setFilters({ ...filters, priority: filters.priority === p ? "" : p })}
                  className={cn(
                    "text-xs px-2.5 py-1 rounded-full border transition",
                    filters.priority === p
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-white/40 dark:border-white/10 hover:bg-white/40 dark:hover:bg-white/10"
                  )}
                >{t(`filters.prio.${p}`)}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{t("filters.date")}</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[
                { k: "overdue", label: t("filters.overdue") },
                { k: "week", label: t("filters.nextWeek") },
              ].map((d) => (
                <button
                  key={d.k}
                  data-testid={`filter-due-${d.k}`}
                  onClick={() => setFilters({ ...filters, dueRange: filters.dueRange === d.k ? "" : d.k })}
                  className={cn(
                    "text-xs px-2.5 py-1 rounded-full border transition",
                    filters.dueRange === d.k
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-white/40 dark:border-white/10 hover:bg-white/40 dark:hover:bg-white/10"
                  )}
                >{d.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">{t("filters.labels")}</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {labels.map((l) => {
                const on = filters.labelIds.includes(l.label_id);
                return (
                  <button
                    key={l.label_id}
                    data-testid={`filter-label-${l.label_id}`}
                    onClick={() =>
                      setFilters({
                        ...filters,
                        labelIds: on
                          ? filters.labelIds.filter(x => x !== l.label_id)
                          : [...filters.labelIds, l.label_id],
                      })
                    }
                    className={cn(
                      "text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1.5 transition",
                      on ? "bg-blue-600 text-white border-blue-600" : "border-white/40 dark:border-white/10 hover:bg-white/40 dark:hover:bg-white/10"
                    )}
                  >
                    <span className={cn("h-2 w-2 rounded-full", LABEL_COLORS[l.color] || "bg-blue-500")} />
                    {l.name}
                  </button>
                );
              })}
              {labels.length === 0 && <span className="text-xs text-muted-foreground">{t("filters.noLabels")}</span>}
            </div>
          </div>
          {active && (
            <Button
              variant="ghost" size="sm"
              data-testid="filters-clear"
              onClick={() => setFilters({ q: "", labelIds: [], priority: "", dueRange: "", onlyMine: false })}
              className="text-xs"
            ><X className="h-3 w-3 mr-1" /> {t("filters.clear")}</Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
